import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function sanitizePhone(phone) {
  if (!phone) return null;
  const cleaned = phone.replace(/[^\d]/g, '');
  if (cleaned.length >= 8 && cleaned.length <= 15) {
    return cleaned;
  }
  return null;
}

export function validatePhone(phone) {
  if (!phone || !phone.trim()) return 'Phone number is required';
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length < 8 || digits.length > 15) {
    return 'Use international format: +31612345678 (8-15 digits)';
  }
  return null; // valid
}

export function getWhatsAppLink(phone) {
  const sanitized = sanitizePhone(phone);
  if (!sanitized) return null;
  return `https://wa.me/${sanitized}`;
}

export function getUrgencyBadgeClass(urgency) {
  switch (urgency) {
    case 'CRITICAL':
      return 'badge-critical';
    case 'TODAY':
      return 'badge-today';
    case 'THIS_WEEK':
      return 'badge-week';
    case 'FLEXIBLE':
    default:
      return 'badge-flexible';
  }
}

export function getUrgencyBorderClass(urgency) {
  switch (urgency) {
    case 'CRITICAL':
      return 'urgency-border-critical';
    case 'TODAY':
      return 'urgency-border-today';
    case 'THIS_WEEK':
      return 'urgency-border-week';
    case 'FLEXIBLE':
    default:
      return 'urgency-border-flexible';
  }
}

// Parse a date string as local midnight, not UTC midnight.
// Returns a Date unchanged. Falls through to native parsing for non-string,
// non-Date inputs and for strings that already carry a time component.
export const parseLocalDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string' || !value) return new Date(value);
  return value.includes('T') ? new Date(value) : new Date(value + 'T00:00:00');
};

/* Spelled out rather than toLocaleDateString('en-GB'): engines disagree on the short month —
   Chrome prints "Sept", Safari "Sep" — so the same gig read differently per browser and
   against the list's "25 SEP". Fixed three-letter forms everywhere. */
const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return `${SHORT_WEEKDAY[date.getDay()]} ${date.getDate()} ${SHORT_MONTH[date.getMonth()]}`;
}

/**
 * Returns a short relative-time hint for a gig date, e.g. "in 7 days",
 * "tomorrow", "today", "yesterday", "in 2 weeks", "3 months ago".
 * Returns null for past dates 30+ days old (stale gigs don't need it).
 */
/* `today` ("YYYY-MM-DD") counts from a given day instead of the device's, e.g. the venue's. */
export function formatRelativeTime(dateStr, { short = false, today: todayStr } = {}) {
  if (!dateStr) return null;
  const target = parseLocalDate(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const today = todayStr ? parseLocalDate(todayStr) : new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays === -1) return 'yesterday';
  if (diffDays > 0) {
    if (diffDays < 14) return short ? `in ${diffDays}d` : `in ${diffDays} days`;
    if (diffDays < 30) {
      const w = Math.floor(diffDays / 7);
      return short ? `in ${w}w` : `in ${w} ${w === 1 ? 'week' : 'weeks'}`;
    }
    const m = Math.floor(diffDays / 30);
    return short ? `in ${m}mo` : `in ${m} ${m === 1 ? 'month' : 'months'}`;
  }
  // past
  const ago = -diffDays;
  if (ago < 14) return short ? `${ago}d ago` : `${ago} days ago`;
  if (ago < 30) {
    const w = Math.floor(ago / 7);
    return short ? `${w}w ago` : `${w} ${w === 1 ? 'week' : 'weeks'} ago`;
  }
  return null; // 30+ days old: drop the hint
}

export function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
}

// Re-exported from profileContract so existing callers keep working.
// The canonical implementations live in constants/profileContract.js.
export { rateTypeLabel as getRateTypeLabel, experienceLabel as getExperienceLevelLabel } from '../constants/profileContract';

export function getDayName(dayOfWeek) {
  const days = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days[dayOfWeek] || '';
}

export function getDayPartLabel(dayPart) {
  switch (dayPart) {
    case 'MORNING':
      return 'Morning';
    case 'AFTERNOON':
      return 'Afternoon';
    case 'EVENING':
      return 'Evening';
    default:
      return dayPart;
  }
}

export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
}

/**
 * Generate a Thomann search URL for gear items
 * @param {string} query - The gear item name to search for
 * @returns {string} - Thomann search URL
 */
export function getThomannSearchUrl(query) {
  if (!query) return '#';
  const encodedQuery = encodeURIComponent(query.trim());
  return `https://www.thomann.de/intl/search_dir.html?sw=${encodedQuery}`;
}
